import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { filterDOMProps } from '../../utils/domUtils';
import { cn } from '../../lib/utils';

// Define variants and sizes using a configuration object
const buttonVariantsConfig = {
  base: 'inline-flex items-center justify-center rounded-lg text-sm font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]',
  variants: {
    variant: {
      default:
        'bg-[var(--color-med-primary)] text-white hover:bg-[var(--color-med-primary-dark)] hover:shadow-premium',
      destructive: 'bg-[var(--color-med-danger)] text-white hover:opacity-90',
      outline: 'bg-white border border-[var(--surface-border)] hover:bg-slate-50 hover:shadow-soft transition-all',
      secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
      ghost: 'hover:bg-slate-100 hover:text-[var(--color-med-primary)]',
      link: 'text-[var(--color-med-primary)] underline-offset-4 hover:underline',
    },
    size: {
      default: 'h-11 px-6 py-2',
      sm: 'h-9 rounded-md px-4',
      lg: 'h-13 rounded-xl px-10 text-base',
      icon: 'h-11 w-11',
    },
  },
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof (typeof buttonVariantsConfig)['variants']['variant'];
  size?: keyof (typeof buttonVariantsConfig)['variants']['size'];
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant = 'default', size = 'default', isLoading = false, children, ...props },
    ref
  ) => {
    const variantClasses = buttonVariantsConfig.variants.variant[variant];
    const sizeClasses = buttonVariantsConfig.variants.size[size];
    const validProps = filterDOMProps(props);

    return (
      <button
        className={cn(buttonVariantsConfig.base, variantClasses, sizeClasses, className)}
        ref={ref}
        disabled={isLoading || props.disabled}
        {...validProps}
      >
        {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {children}
      </button>
    );
  }
);
Button.displayName = 'Button';

export { Button };
